import { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { ShieldAlert, X } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import type { BlockReason } from '@/v2/moderation/promptBlocklist';

/**
 * Composer-level refusal dialog. Rendered in place of starting a generation —
 * nothing is sent to fal or HeyGen while this is open.
 *
 * Modal rather than an inline banner: a refusal that sits under the composer is
 * easy to miss and reads like a validation hint. This blocks the flow, so the
 * user has to acknowledge it before retrying. Shell matches v2/dialogs.tsx
 * (same z-index, backdrop, radius and animations) so it feels native.
 *
 * NOTE: `useTranslation('v2')` — V2 copy lives in its own i18next NAMESPACE
 * (see v2/i18n/index.ts). The default namespace is V1's, where these keys do
 * not exist, so omitting 'v2' renders the raw key ("moderation.title").
 *
 * Deliberately does NOT echo the matched term back at the user: repeating an
 * explicit phrase into the UI is its own problem, and naming the exact trigger
 * word is a free hint for anyone probing the filter.
 */
export function PromptBlockedNotice({
  reason,
  onClose,
}: {
  reason: BlockReason | null;
  onClose: () => void;
}) {
  const { t } = useTranslation('v2');

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' || e.key === 'Enter') onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  if (!reason) return null;

  const body =
    reason === 'minor_sexual' ? t('moderation.blockedMinor') : t('moderation.blocked');

  return createPortal(
    <div
      className="fixed inset-0 z-[130] flex items-center justify-center p-4 bg-black/75 backdrop-blur-md animate-fade-in"
      onClick={onClose}
    >
      <div
        role="alertdialog"
        aria-modal="true"
        aria-label={t('moderation.title')}
        className="w-full max-w-sm rounded-3xl bg-[#15151d] border border-white/10 p-6 shadow-2xl animate-slide-up"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start gap-3.5">
          <span className="w-10 h-10 rounded-2xl flex items-center justify-center shrink-0 bg-amber-400/15 border border-amber-400/30">
            <ShieldAlert className="w-5 h-5 text-amber-300" />
          </span>
          <div className="flex-1 min-w-0 pt-0.5">
            <h3 className="text-base font-bold text-text-primary mb-1">
              {t('moderation.title')}
            </h3>
            <p className="text-sm text-text-secondary leading-relaxed">{body}</p>
          </div>
          <button
            onClick={onClose}
            aria-label={t('moderation.dismiss')}
            className="shrink-0 w-8 h-8 rounded-full hover:bg-white/10 flex items-center justify-center text-text-tertiary hover:text-text-primary transition-colors"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="flex items-center justify-end mt-5">
          <button
            onClick={onClose}
            className="px-5 py-2.5 rounded-full text-sm font-semibold text-white transition-all hover:opacity-95 bg-gradient-to-r from-primary to-accent hover:shadow-glow"
          >
            {t('moderation.dismiss')}
          </button>
        </div>
      </div>
    </div>,
    document.body,
  );
}
